/**
 * The one semantic token layer for the signed-in app.
 *
 * Every area used to ship its own hand-transcribed hex table (shell, command
 * dashboards, Brand Home, Brands & Vendors, Data Ingestion), and they
 * disagreed on accent blue, success green, card radius and body font. Those
 * files still exist so their importers keep working, but each is now a thin
 * mapping onto the values here. `appTheme.ts` reads the same values, so the
 * MUI palette and the area tokens can no longer drift apart.
 *
 * Signal colours are the muted set the dashboard handoffs chose over MUI's
 * saturated defaults: `ink` for text, `solid` for dots, bars and fills,
 * `tint` for chip and banner backgrounds, `border` for their outlines, and
 * `row` for a whole-row wash.
 *
 * The public marketing site keeps its own dark theme (`marketingTokens.ts`).
 */
export const tokens = {
  font: {
    sans: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
  },
  surface: {
    /** The page ground the shell paints behind every signed-in page. */
    page: '#eef1f6',
    card: '#ffffff',
    /** Rails, table heads, outer cards that hold inner cards. */
    sunken: '#f8fafc',
    /** Hover rows, expanded rows, sub-surfaces inside a card. */
    subtle: '#fbfcfe',
    /** Neutral chip fill. */
    tint: '#f1f5f9',
    /** Dark command header, selected filter chips. */
    header: '#0f172a',
  },
  line: {
    default: '#e2e8f0',
    strong: '#dbe2ea',
    hairline: '#f1f5f9',
    faint: '#f6f8fb',
  },
  ink: {
    primary: '#0f172a',
    body: '#334155',
    bodyAlt: '#475569',
    muted: '#64748b',
    faint: '#94a3b8',
    disabled: '#cbd5e1',
    onDark: '#e2e8f0',
    onDarkBright: '#f8fafc',
    inverse: '#ffffff',
  },
  accent: {
    main: '#2563eb',
    hover: '#1d4ed8',
    tint: '#eff6ff',
    tintStrong: '#dbe5fb',
    border: '#c7dbfe',
  },
  signal: {
    positive: { ink: '#2f6b5a', solid: '#4f9d86', tint: '#f1f7f4', border: '#cbe3d9', row: '#f6faf8' },
    caution: { ink: '#96601f', solid: '#d8a765', tint: '#fbf5ec', border: '#ecd9bc', row: '#fdfaf4' },
    problem: { ink: '#a33a30', solid: '#c1584b', tint: '#fdf3f1', border: '#f3d3cc', row: '#fdf8f7' },
    info: { ink: '#1d4ed8', solid: '#2563eb', tint: '#eff6ff', border: '#c7dbfe', row: '#f5f8ff' },
    neutral: { ink: '#475569', solid: '#94a3b8', tint: '#f1f5f9', border: '#dbe2ea', row: '#f8fafc' },
  },
  radius: {
    card: 12,
    /** Outer cards, menus, drawers, logo frames. */
    panel: 14,
    /** Buttons and inputs. */
    control: 10,
    small: 8,
    chip: 6,
    pill: 20,
  },
  shadow: {
    card: '0 1px 2px rgba(15,23,42,.05)',
    menu: '0 14px 34px rgba(15,23,42,.14), 0 2px 6px rgba(15,23,42,.06)',
    flyout: '0 16px 40px rgba(15,23,42,.16)',
    overlay: '0 30px 70px rgba(15,23,42,.28)',
  },
} as const;

export type SignalTone = keyof typeof tokens.signal;

/** The five status tones every shared chip, badge and banner speaks. */
export const signalTones: SignalTone[] = ['positive', 'caution', 'problem', 'info', 'neutral'];
